import css from "@css/Components.module.scss"
import { CSSProperties, HTMLAttributes, JSX, useMemo } from "react"
import cn from "classnames"

const FilledStar = ({ size = 30 }) => (
    <svg width={size} height={size} viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className={css.svgFilled}>
        <path
            d="M6.93493 0.83874C7.17868 0.45052 7.7444 0.45052 7.98814 0.83874L9.84183 3.79117C9.92703 3.92687 10.0613 4.02444 10.2167 4.06354L13.5975 4.91415C14.042 5.026 14.2168 5.56403 13.9229 5.91581L11.6878 8.59112C11.5851 8.71409 11.5338 8.87197 11.5446 9.03183L11.7804 12.51C11.8113 12.9673 11.3537 13.2998 10.9283 13.129L7.69323 11.83C7.54454 11.7703 7.37854 11.7703 7.22985 11.83L3.99479 13.129C3.5694 13.2998 3.11173 12.9673 3.14272 12.51L3.37845 9.03183C3.38928 8.87197 3.33799 8.71409 3.23526 8.59112L1.00015 5.91581C0.706255 5.56403 0.881072 5.026 1.32561 4.91415L4.70636 4.06354C4.86175 4.02444 4.99604 3.92687 5.08125 3.79117L6.93493 0.83874Z"
            fill="#FFE27A"
        />
    </svg>
)
const EmptyStar = ({ size = 30 }) => (
    <svg width={size} height={size} viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className={css.svgEmpty}>
        <path
            d="M6.93493 0.83874C7.17868 0.45052 7.7444 0.45052 7.98814 0.83874L9.84183 3.79117C9.92703 3.92687 10.0613 4.02444 10.2167 4.06354L13.5975 4.91415C14.042 5.026 14.2168 5.56403 13.9229 5.91581L11.6878 8.59112C11.5851 8.71409 11.5338 8.87197 11.5446 9.03183L11.7804 12.51C11.8113 12.9673 11.3537 13.2998 10.9283 13.129L7.69323 11.83C7.54454 11.7703 7.37854 11.7703 7.22985 11.83L3.99479 13.129C3.5694 13.2998 3.11173 12.9673 3.14272 12.51L3.37845 9.03183C3.38928 8.87197 3.33799 8.71409 3.23526 8.59112L1.00015 5.91581C0.706255 5.56403 0.881072 5.026 1.32561 4.91415L4.70636 4.06354C4.86175 4.02444 4.99604 3.92687 5.08125 3.79117L6.93493 0.83874Z"
            fill="#30254C"
            stroke="#FFE27A"
            strokeWidth="0.5"
        />
    </svg>
)

function getNumberParts(number: number) {
    // Using Math.floor to get the integer part
    const integerPart = Math.floor(number)

    // Using % (modulo) and parseFloat to get the fractional part
    const fractionalPart = parseFloat((number - integerPart).toFixed(2))

    return [integerPart, fractionalPart]
}

type Props = {
    size?: number
    gap?: number
    rating: number
    withShort?: boolean
    /* onChange?: (rating: number) => void*/
    maxRating?: number
    className?: string
} & Partial<Omit<HTMLAttributes<HTMLDivElement>, "className" | "children" | "style">>

export function StartRating({ rating = 2.5, maxRating = 5, size = 30, gap = 0, className, withShort, ...rest }: Props) {
    const [ratingInt, ratingFr] = getNumberParts(rating)
    const style = useMemo(() => {
        const vars: CSSProperties = {}
        if (size) {
            vars["--size"] = size + "px"
        }
        if (gap) {
            vars["--gap"] = gap + "px"
        }
        return vars
    }, [size, gap])
    let short: JSX.Element
    if (withShort) {
        short = (
            <div className={cn(css.shortStars, className)} data-variant="short">
                <span className={css.current}>
                    {rating} / {maxRating}
                </span>
            </div>
        )
    }
    return (
        <>
            {short}
            <div style={style} className={cn(css.starRating, className)} {...rest} data-variant="default">
                {[...Array(maxRating)].map((_, index) => {
                    const i = index
                    const percent = ratingInt > i ? 100 : ratingInt === i ? ratingFr * 100 : 0
                    const vars: CSSProperties = {
                        "--percent": percent + "%"
                    }
                    return (
                        <div className={css.starHolder} style={vars} key={index}>
                            {percent !== 0 && (
                                <div className={css.filledWrap}>
                                    <FilledStar />
                                </div>
                            )}
                            {percent !== 100 && <EmptyStar />}
                        </div>
                    )
                })}
            </div>
        </>
    )
}
